import React, { useState, useEffect, useCallback, useContext, useMemo, memo } from "react"; // Import memo
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "../common/Icons";
import {
  HomeIcon,
  GamepadIcon, // Used for all game types
  HistoryIcon,
  WalletIcon,
  TrendingUpIcon,
  HelpCircleIcon,
  DollarSignIcon,
  UserIcon,
  PhoneCallIcon,
  LogOutIcon,
} from "../common/Icons";
import { LanguageContext } from "../contexts/LanguageContext";
import { UserContext } from "../contexts/UserContext";

/**
 * Sidebar component for navigation within the application.
 * It displays user information, navigation links, and settings.
 * It's responsive, with different behavior for mobile and desktop views.
 * Memoized to prevent unnecessary re-renders.
 */
const Sidebar = memo(({ isOpen, toggleSidebar = () => {}, setCurrentPage }) => {
  // Wrap the component in memo
  // Contexts for language and user data
  const { language } = useContext(LanguageContext);
  const { userData, userLoading, userError, refreshUserData } =
    useContext(UserContext);
  const navigate = useNavigate();

  // Memoized translations for efficient rendering - MOVED TO TOP
  const t = useMemo(() => {
  const translations = {
    en: {
      player: "Player",
      guest: "Guest",
      loading: "Loading...",
      home: "Home",
      contact: "Help",
      leaderboard: "Top",
      howToPlay: "Guide",
      referralIncome: "Referral",
      wallet: "Wallet",
      profile: "Profile",
      history: "History",
      logout: "Logout",
      bingoGame: "Bingo",
      moneyBagGame: "Money Bag", // Re-enabled
      diamondGame: "Diamond", // Re-enabled
      settings: "Settings",
      kenoGame: "Fast Keno", // Added translation for Fast Keno
    },
    am: {
      player: "ተጫዋች",
      guest: "እንግዳ",
      loading: "በመጫን ላይ...",
      home: "መነሻ",
      contact: "እርዳታ",
      leaderboard: "አሸናፊዎች",
      howToPlay: "መመሪያ",
      referralIncome: "ሪፈራል",
      wallet: "ቦርሳ",
      profile: "መገለጫ",
      history: "ታሪክ",
      logout: "ውጣ",
      bingoGame: "ቢንጎ",
      moneyBagGame: "ገንዘብ", // Re-enabled
      diamondGame: "አልማዝ", // Re-enabled
      settings: "ቅንብር",
      kenoGame: "ፈጣን ኬኖ", // Added translation for Fast Keno
    },
    or: {
      player: "Taphataa",
      guest: "Daawwataa",
      loading: "Fe’aa jirti...",
      home: "Jalqaba",
      contact: "Deeggarsa",
      leaderboard: "Ol’aantuu",
      howToPlay: "Taphni",
      referralIncome: "Referral",
      wallet: "Boorsaa",
      profile: "Ibsaa",
      history: "Seenaa",
      logout: "Ba’i",
      bingoGame: "Bingo",
      moneyBagGame: "Maallaqa", // Re-enabled
      diamondGame: "Diamond", // Re-enabled
      settings: "Qindaa’ina",
      kenoGame: "Keno Ariifachiisaa", // Added translation for Fast Keno
    },
    ti: {
      player: "ተጫዋቲ",
      guest: "ጎብኚ",
      loading: "በመጫን...",
      home: "መጀመርያ",
      contact: "ናገናን",
      leaderboard: "አሸናፊ",
      howToPlay: "መመሪያ",
      referralIncome: "ሪፈራል",
      wallet: "ቦርሳ",
      profile: "መገለጺ",
      history: "ታሪኽ",
      logout: "ውጽኢት",
      bingoGame: "ቢንጎ",
      moneyBagGame: "ናይ ገንዘብ ከረጢት", // Re-enabled
      diamondGame: "ኣልማዝ", // Re-enabled
      settings: "ቅንብር",
      kenoGame: "ፈጣን ኬኖ", // Added translation for Fast Keno
    },
    wo: {
      player: "Taphataa",
      guest: "Bariisaa",
      loading: "Fe’aa jiruu...",
      home: "Mana",
      contact: "Tumsaa",
      leaderboard: "Top Taphoota",
      howToPlay: "Akkaataa",
      referralIncome: "Referral",
      wallet: "Boorsaa",
      profile: "Fayyadamaa",
      history: "Seenaa",
      logout: "Ba’i",
      bingoGame: "Bingo",
      moneyBagGame: "Maallaqa", // Re-enabled
      diamondGame: "Diamond", // Re-enabled
      settings: "Qindaa’ina",
      kenoGame: "Fast Keno", // Added translation for Fast Keno
    },
  };

  return (key) => translations[language]?.[key] ?? translations.en[key];
}, [language]);


  // Define navigation items with their paths and icons - MOVED TO TOP
  const navItems = useMemo(
    () => [
      { key: "home", path: "/dashboard", icon: <HomeIcon /> },
      { key: "kenoGame", path: "/fast-keno", icon: <GamepadIcon /> }, // Added Fast Keno
      { key: "bingoGame", path: "/stake-selection", icon: <GamepadIcon /> },
      { key: "moneyBagGame", path: "/money-bag-game", icon: <GamepadIcon /> }, // Re-enabled
      { key: "diamondGame", path: "/diamond-game", icon: <GamepadIcon /> }, // Re-enabled
      { key: "history", path: "/history", icon: <HistoryIcon /> },
      { key: "wallet", path: "/wallet", icon: <WalletIcon /> },
      { key: "leaderboard", path: "/leaderboard", icon: <TrendingUpIcon /> },
      { key: "howToPlay", path: "/how-to-play", icon: <HelpCircleIcon /> },
      {
        key: "referralIncome",
        path: "/referral-income",
        icon: <DollarSignIcon />,
      },
    ],
    [t]
  ); // Added 't' to the dependency array

  // Effect to refresh user data when the sidebar opens
  useEffect(() => {
    if (isOpen) {
      refreshUserData();
    }
  }, [isOpen, refreshUserData]);

  // Effect to handle clicks outside the sidebar and Escape key press for closing
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector(".sidebar-container");
      // Close sidebar if clicking outside and sidebar is open
      if (isOpen && sidebar && !sidebar.contains(event.target)) {
        toggleSidebar();
      }
    };

    const handleEscape = (event) => {
      // Close sidebar if Escape key is pressed and sidebar is open
      if (event.key === "Escape" && isOpen) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    // Prevent body scrolling when sidebar is open
    document.body.style.overflow = isOpen ? "hidden" : "auto";

    // Cleanup event listeners and body overflow on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, toggleSidebar]);

  /**
   * Handles navigation to a new path and closes the sidebar.
   * @param {string} path - The path to navigate to.
   */
  const handleNavigation = (path) => {
    navigate(path);
    toggleSidebar(); // Close sidebar after navigation
  };

  /**
   * Handles user logout by removing the token and navigating to the login page.
   */
  const handleLogout = () => {
    localStorage.removeItem("token");
    handleNavigation("/login");
  };

  // Handle user error: if 401 or 403, clear token and redirect to login
  if (userError) {
    if (
      userError.message &&
      (userError.message.includes("401") || userError.message.includes("403"))
    ) {
      localStorage.removeItem("token");
      navigate("/login");
    }
    return null; // Don't render sidebar if there's a critical user error
  }

  return (
    <>
      {/* Overlay for mobile view when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[999]"
          onClick={toggleSidebar} // Close sidebar when overlay is clicked
        />
      )}

      {/* Sidebar container */}
      <aside
        className={`sidebar-container fixed top-0 left-0 w-64 bg-[#2f3650] shadow-xl transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out z-[1000] overflow-y-auto max-h-screen`}
      >
        {/* User profile section in sidebar header */}
        <div className="p-4 bg-[#3c4561] text-white flex items-center justify-between border-b border-[#4a5675]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-full overflow-hidden flex justify-center items-center">
              {/* Placeholder for user avatar */}
              <img
                src="https://placehold.co/40x40/CCCCCC/FFFFFF?text=U"
                alt="User Avatar"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-semibold whitespace-nowrap">
                {userData?.fullName || t("guest")}
              </p>
              <p className="text-xs text-gray-300">
                {t("player")} ID: {userData?.id || "N/A"}
              </p>
            </div>
          </div>
          {/* Button to close the sidebar */}
          <button
            onClick={toggleSidebar}
            className="text-white hover:bg-[#4a5675] p-1 rounded-md transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Main navigation links */}
        <nav className="py-4">
          <ul>
            {navItems.map((item) => (
              <li key={item.key} className="mb-1 mx-2">
                <button
                  onClick={() => handleNavigation(item.path)}
                  className="flex items-center w-full px-4 py-3 text-gray-300 hover:bg-[#3c4561] rounded-lg"
                >
                  {item.icon}
                  <span className="ml-3">{t(item.key)}</span>
                </button>
              </li>
            ))}
          </ul>

          {/* Settings section header */}
          <div className="mt-8 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {t("settings")}
          </div>

          {/* Settings navigation links */}
          <ul className="mt-2">
            {[
              { path: "/profile", icon: <UserIcon />, label: t("profile") },
            ].map(({ path, icon, label }) => (
              <li key={path} className="mb-1 mx-2">
                <button
                  onClick={() => handleNavigation(path)}
                  className="flex items-center w-full px-4 py-3 text-gray-300 hover:bg-[#3c4561] rounded-lg"
                >
                  {icon}
                  <span className="ml-3">{label}</span>
                </button>
              </li>
            ))}
            <li className="mb-1 mx-2">
              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-red-400 hover:bg-[#3c4561] rounded-lg"
              >
                <LogOutIcon className="mr-3 w-5 h-5" />
                {t("logout")}
              </button>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
});

export default Sidebar;
